import { scrubRoute, scrubText } from "./scrub";

// ---------------------------------------------------------------------------
// The browser reporter. Browsers never post to Conductor (the doc's rule):
// window errors, unhandled rejections, error boundaries, rejected Server
// Actions, the page load (with its Web Vitals) and navigation timings go to
// OUR route,
// POST /api/telemetry, which scrubs them again, adds the session's ids and
// queues them into the server's batcher. CSP `connect-src 'self'` stays.
//
// On only when the root layout marks <html data-telemetry="1"> (the server
// has telemetry configured). Batched: every 5 s, at 20 events, and with
// sendBeacon when the page is hidden. Bounded per page load (errors are
// deduplicated), and it never throws.
//
// The page load is held until the page is hidden (or PAGELOAD_MAX_HOLD_MS):
// Conductor takes Web Vitals only as `measurements` of a pageload timing, and
// LCP / CLS / INP are final only then. Vitals reported after it went out
// (the tab came back and was hidden again) are not sent.
// ---------------------------------------------------------------------------

type BrowserEvent = Record<string, unknown> & { type: "error" | "transaction" };
type MeasurementName = "ttfb" | "fcp" | "lcp" | "inp" | "cls";

const ENDPOINT = "/api/telemetry";
const FLUSH_MS = 5_000;
const FLUSH_AT = 20;
const MAX_PER_POST = 50;
const MAX_POST_BYTES = 60_000;
const MAX_ERRORS_PER_PAGE = 25;
const MAX_EVENTS_PER_PAGE = 300;
const PAGELOAD_MAX_HOLD_MS = 30 * 60_000;

interface ReporterState {
  queue: BrowserEvent[];
  timer: ReturnType<typeof setTimeout> | null;
  errors: number;
  total: number;
  seen: Set<string>;
  listening: boolean;
  /** The page load's timing, held for its vitals; null once sent. */
  pageload: BrowserEvent | null;
  pageloadSent: boolean;
  vitals: Partial<Record<MeasurementName, number>>;
}

const w = (typeof window !== "undefined" ? window : undefined) as
  | (Window & { __circuitoTelemetry?: ReporterState; __circuitoTelemetryOn?: boolean })
  | undefined;

function state(): ReporterState | null {
  if (!w) return null;
  w.__circuitoTelemetry ??= {
    queue: [],
    timer: null,
    errors: 0,
    total: 0,
    seen: new Set(),
    listening: false,
    pageload: null,
    pageloadSent: false,
    vitals: {},
  };
  return w.__circuitoTelemetry;
}

/**
 * Whether the server has telemetry on (<html data-telemetry="1">). Remembered
 * once seen: global-error.tsx replaces <html>, and its report must still go.
 */
export function browserTelemetryEnabled(): boolean {
  if (!w) return false;
  if (w.__circuitoTelemetryOn) return true;
  try {
    w.__circuitoTelemetryOn = document.documentElement.dataset.telemetry === "1";
  } catch {
    w.__circuitoTelemetryOn = false;
  }
  return w.__circuitoTelemetryOn;
}

function uuid(): string {
  try {
    return crypto.randomUUID();
  } catch {
    const b = new Uint8Array(16);
    crypto.getRandomValues(b);
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
  }
}

/** The current route as a pattern-ish path (ids → [id], never a query). */
export function currentRoute(): string | undefined {
  try {
    return scrubRoute(window.location.pathname);
  } catch {
    return undefined;
  }
}

function post(events: BrowserEvent[], beacon: boolean): void {
  if (!events.length) return;
  const body = JSON.stringify({ events });
  if (body.length > MAX_POST_BYTES) {
    if (events.length === 1) return;
    const half = Math.ceil(events.length / 2);
    post(events.slice(0, half), beacon);
    post(events.slice(half), beacon);
    return;
  }
  try {
    if (beacon && typeof navigator.sendBeacon === "function") {
      if (navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "application/json" }))) return;
    }
    void fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      credentials: "same-origin",
      cache: "no-store",
    }).catch(() => {});
  } catch {
    // Never into the page.
  }
}

/** Queues the held page load with the vitals seen so far (once per page). */
function releasePageload(s: ReporterState): void {
  if (!s.pageload || s.pageloadSent) return;
  s.pageloadSent = true;
  const event = s.pageload;
  s.pageload = null;
  if (Object.keys(s.vitals).length) event.measurements = { ...s.vitals };
  s.queue.push(event);
}

export function flushBrowserTelemetry(beacon = false, releaseHeld = beacon): void {
  const s = state();
  if (!s) return;
  if (releaseHeld) releasePageload(s);
  if (s.timer) {
    clearTimeout(s.timer);
    s.timer = null;
  }
  while (s.queue.length) post(s.queue.splice(0, MAX_PER_POST), beacon);
}

function listen(s: ReporterState): void {
  if (s.listening) return;
  s.listening = true;
  // The held page load goes out AFTER the other hide listeners ran (Web
  // Vitals reports its final CLS / INP there): a timer on visibilitychange
  // (tab switch), and pagehide, which browsers fire after visibilitychange
  // when the page is left.
  const onHide = () => {
    if (document.visibilityState !== "hidden") return;
    flushBrowserTelemetry(true, false);
    setTimeout(() => flushBrowserTelemetry(true), 0);
  };
  document.addEventListener("visibilitychange", onHide);
  window.addEventListener("pagehide", () => flushBrowserTelemetry(true));
}

function push(event: BrowserEvent, force = false): void {
  try {
    if (!force && !browserTelemetryEnabled()) return;
    const s = state();
    if (!s || s.total >= MAX_EVENTS_PER_PAGE) return;
    s.total++;
    listen(s);
    s.queue.push(event);
    if (s.queue.length >= FLUSH_AT) flushBrowserTelemetry();
    else if (!s.timer) s.timer = setTimeout(() => flushBrowserTelemetry(), FLUSH_MS);
  } catch {
    // Never into the page.
  }
}

export interface BrowserErrorOptions {
  handled: boolean;
  level?: "fatal" | "error" | "warning";
  digest?: string;
  ref?: string;
  /** Where in the UI (e.g. "error-boundary", "composer"); a tag. */
  where?: string;
  /**
   * Send even without <html data-telemetry>: global-error.tsx, whose document
   * may never have had it (a root layout crash serves it as a new page). The
   * tunnel answers 204 and drops it when telemetry is off.
   */
  force?: boolean;
}

/** One browser error (window.onerror, a rejection, an error boundary, a rejected action). */
export function reportBrowserError(error: unknown, opts: BrowserErrorOptions): void {
  try {
    if (!opts.force && !browserTelemetryEnabled()) return;
    const s = state();
    if (!s || s.errors >= MAX_ERRORS_PER_PAGE) return;
    const e = error instanceof Error ? error : null;
    const type = (e?.name || (typeof error === "object" && error ? "Object" : typeof error) || "Error").slice(0, 200);
    const message = scrubText(e ? e.message : typeof error === "string" ? error : "non-error value", 2000);
    // Raw frames: the server parses them into frames and scrubs each one.
    const stack = typeof e?.stack === "string" ? e.stack.slice(0, 16_000) : undefined;
    const key = `${type}|${message}|${(e?.stack ?? "").split("\n")[1] ?? ""}|${opts.digest ?? ""}`;
    if (s.seen.has(key)) return;
    s.seen.add(key);
    s.errors++;
    const digest = (error as { digest?: unknown })?.digest;
    push({
      type: "error",
      event_id: uuid(),
      timestamp: new Date().toISOString(),
      level: opts.level ?? "error",
      handled: opts.handled,
      source: "browser",
      ...(opts.digest || typeof digest === "string" ? { digest: opts.digest ?? digest } : {}),
      ...(opts.ref ? { ref: opts.ref } : {}),
      exception: { type, message, ...(stack ? { stack_raw: stack } : {}) },
      route: currentRoute(),
      ...(opts.where ? { tags: { where: opts.where } } : {}),
    }, opts.force);
  } catch {
    // Never into the page.
  }
}

export interface VitalMetric {
  id: string;
  name: string;
  value: number;
  rating?: string;
  navigationType?: string;
}

const VITALS = new Set(["LCP", "INP", "CLS", "FCP", "TTFB"]);

/**
 * One Web Vital from useReportWebVitals (FID and others are skipped): kept
 * for the page load's `measurements` (the latest value of each wins).
 */
export function reportWebVital(metric: VitalMetric): void {
  try {
    if (!VITALS.has(metric.name) || !Number.isFinite(metric.value) || metric.value < 0) return;
    if (!browserTelemetryEnabled()) return;
    const s = state();
    if (!s || s.pageloadSent) return;
    s.vitals[metric.name.toLowerCase() as MeasurementName] = Math.round(metric.value * 1000) / 1000;
  } catch {
    // Never into the page.
  }
}

/**
 * A page load or a client-side navigation, timed in the browser. A page load
 * is held for its vitals until the page is hidden (or PAGELOAD_MAX_HOLD_MS).
 */
export function reportNavigation(op: "browser.pageload" | "browser.navigation", startEpochMs: number, durationMs: number): void {
  if (!Number.isFinite(durationMs) || durationMs < 0 || durationMs > 10 * 60_000) return;
  const event: BrowserEvent = {
    type: "transaction",
    event_id: uuid(),
    timestamp: new Date().toISOString(),
    op,
    start: new Date(startEpochMs).toISOString(),
    duration_ms: Math.round(durationMs * 10) / 10,
    status: "ok",
    route: currentRoute(),
  };
  if (op === "browser.navigation") {
    push(event);
    return;
  }
  try {
    if (!browserTelemetryEnabled()) return;
    const s = state();
    if (!s || s.pageload || s.pageloadSent || s.total >= MAX_EVENTS_PER_PAGE) return;
    s.total++;
    listen(s);
    s.pageload = event;
    setTimeout(() => {
      releasePageload(s);
      flushBrowserTelemetry();
    }, PAGELOAD_MAX_HOLD_MS);
  } catch {
    // Never into the page.
  }
}
